/**
 * Code, as a thing to read rather than a screenshot of one.
 *
 * The page's argument is that the engineering underneath is real, and the
 * only honest way to make that argument is to show the code. So it is real
 * text: selectable, searchable, and readable by a screen reader, in a block
 * an editor can change without a rebuild.
 *
 * Highlighting is a small hand-rolled tokeniser rather than a library. Two
 * reasons: a syntax-highlighting bundle is heavier than everything else this
 * page loads, and the palette has to come from the design tokens, which a
 * library's theme would fight. It knows Python and Dockerfile, which is what
 * this project has.
 */

import type { BlockRenderer } from '@remidus/web-core'

const PY_KEYWORDS = new Set([
  'and', 'as', 'assert', 'async', 'await', 'break', 'class', 'continue', 'def',
  'del', 'elif', 'else', 'except', 'False', 'finally', 'for', 'from', 'global',
  'if', 'import', 'in', 'is', 'lambda', 'None', 'nonlocal', 'not', 'or', 'pass',
  'raise', 'return', 'True', 'try', 'while', 'with', 'yield', 'self',
])
const DOCKER_KEYWORDS = new Set([
  'FROM', 'RUN', 'CMD', 'COPY', 'ADD', 'WORKDIR', 'ENV', 'EXPOSE', 'ENTRYPOINT',
  'ARG', 'VOLUME', 'USER', 'LABEL', 'HEALTHCHECK',
])

/** One token, as a span with a class the stylesheet colours. */
function span(text: string, kind?: string): Node {
  if (!kind) return document.createTextNode(text)
  const el = document.createElement('span')
  el.className = `skin-code__${kind}`
  el.textContent = text
  return el
}

/** Split a line into tokens. Deliberately shallow: comments, strings, numbers,
 *  keywords, and the name after `def`/`class`. Anything it cannot classify is
 *  left as plain text, which is the right failure. */
function tokenise(line: string, lang: string): Node[] {
  const out: Node[] = []
  const comment = lang === 'docker' ? /^\s*#/ : /(^|\s)#/
  const hash = line.search(comment)
  let body = line
  let tail: Node | null = null
  // A `#` inside a string is not a comment; the cheapest correct-enough test
  // is whether the quotes before it are balanced.
  if (hash >= 0) {
    const before = line.slice(0, hash)
    const quotes = (before.match(/["']/g) ?? []).length
    if (quotes % 2 === 0) {
      body = line.slice(0, hash)
      tail = span(line.slice(hash), 'comment')
    }
  }

  const pattern = /("""[\s\S]*?"""|'''[\s\S]*?'''|"[^"]*"|'[^']*'|\b\d[\d._]*\b|\b\w+\b|\W+)/g
  let previous = ''
  for (const piece of body.match(pattern) ?? []) {
    if (/^["']/.test(piece)) out.push(span(piece, 'string'))
    else if (/^\d/.test(piece)) out.push(span(piece, 'number'))
    else if (lang === 'docker' && DOCKER_KEYWORDS.has(piece)) out.push(span(piece, 'keyword'))
    else if (lang !== 'docker' && PY_KEYWORDS.has(piece)) out.push(span(piece, 'keyword'))
    else if (previous === 'def' || previous === 'class') out.push(span(piece, 'name'))
    else out.push(span(piece))
    if (/\w/.test(piece)) previous = piece
  }
  if (tail) out.push(tail)
  return out
}

export const renderCode: BlockRenderer = (block) => {
  const data = block.data ?? {}
  const source = String(data.code ?? '').replace(/\s+$/, '')
  if (!source) return null

  const lang = String(data.lang ?? 'python')
  const figure = document.createElement('figure')
  figure.className = 'skin-code'

  const bar = document.createElement('figcaption')
  bar.className = 'skin-code__bar'
  const file = document.createElement('span')
  file.className = 'skin-code__file'
  file.textContent = String(data.file ?? '')
  const note = document.createElement('span')
  note.className = 'skin-code__note'
  note.textContent = String(data.note ?? '')
  bar.append(file, note)

  const pre = document.createElement('pre')
  pre.className = 'skin-code__pre'
  pre.tabIndex = 0
  const codeEl = document.createElement('code')
  codeEl.setAttribute('data-lang', lang)
  for (const [index, line] of source.split('\n').entries()) {
    const row = document.createElement('span')
    row.className = 'skin-code__line'
    row.append(...tokenise(line, lang))
    codeEl.append(row)
    if (index < source.split('\n').length - 1) codeEl.append(document.createTextNode('\n'))
  }
  pre.append(codeEl)

  figure.append(bar, pre)
  return figure
}
